import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi } from '../../api/portalApi';
import { useRuntimeConfig } from '../../context/RuntimeConfigContext';
import { useToast } from '../../components/Toast';
import Field from '../../components/settings/Field';
import type { UserSettings } from '../../types';

export default function MyGitSettingsPage() {
  const { instance } = useMsal();
  const cfg           = useRuntimeConfig();
  const api            = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
  const { showToast }  = useToast();

  const [userSettings, setUserSettings] = useState<UserSettings>({});
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');

  useEffect(() => {
    api.getUserSettings()
      .then(u => setUserSettings(u ?? {}))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.saveUserSettings(userSettings);
      showToast('Settings saved successfully.', 'success');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="page-loading"><span className="spinner" />Loading settings…</div>;

  return (
    <div>
      <Link to="/settings/profile" className="settings-back-link">← My Settings</Link>

      {error && <div className="alert alert-error">{error}</div>}

      <form className="settings-form" onSubmit={handleSubmit}>
        <div className="settings-card">
          <h2 className="settings-section-title">GitHub Integration</h2>
          <p className="settings-section-desc">
            Used by the Web Automation Coder's "Push to repo" button. If no personal token is set,
            the organisation's shared token is used automatically.
          </p>

          {userSettings.githubToken && (
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Which GitHub token should be used?
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 'normal' }}>
                  <input
                    type="radio"
                    name="githubTokenSource"
                    checked={userSettings.githubUseOrgToken === true}
                    onChange={() => setUserSettings(u => ({ ...u, githubUseOrgToken: true }))}
                  />
                  Use the organisation's shared token (set by your admin)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 'normal' }}>
                  <input
                    type="radio"
                    name="githubTokenSource"
                    checked={userSettings.githubUseOrgToken !== true}
                    onChange={() => setUserSettings(u => ({ ...u, githubUseOrgToken: false }))}
                  />
                  Use my personal token (configured below)
                </label>
              </div>
            </div>
          )}

          <div className="form-grid">
            <Field
              label="Personal Access Token"
              placeholder="Your personal GitHub PAT (leave blank to always use the org token)"
              value={userSettings.githubToken ?? ''}
              onChange={v => setUserSettings(u => ({ ...u, githubToken: v }))}
              secret
            />
          </div>
        </div>

        <div className="settings-footer">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
